import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsConditions = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const lastUpdated = '12 June 2026';

  const sections = [
    {
      title: '1. Introduction',
      body: [
        'These Terms and Conditions ("Terms") govern your access to and use of the GovernWell website and platform ("the Service"), operated by GovernWell ("GovernWell", "we", "us", or "our").',
        'By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, you must not use the Service.',
      ],
    },
    {
      title: '2. The Service',
      body: [
        'GovernWell is a self-led governance review platform that provides tools, frameworks, assessments, and reporting to help organisations conduct governance reviews internally.',
        'GovernWell provides a structured methodology and software tools only. We do not provide professional, legal, regulatory, or consultancy advice, and the outputs of the Service should not be relied upon as such.',
      ],
    },
    {
      title: '3. Accounts and eligibility',
      list: [
        'You must provide accurate and complete information when creating an account.',
        'You are responsible for maintaining the confidentiality of your login credentials.',
        'You are responsible for all activity that occurs under your account.',
        'You must notify us promptly of any unauthorised use of your account.',
        'You must be authorised to act on behalf of any organisation you register.',
      ],
    },
    {
      title: '4. Acceptable use',
      body: [
        'You agree to use the Service only for lawful purposes and in accordance with these Terms. You must not misuse the Service, attempt to gain unauthorised access, interfere with its operation, or use it in any way that could damage, disable, or impair the Service.',
      ],
      list: [
        'Do not copy, resell, or redistribute the Service without our written permission.',
        'Do not upload unlawful, harmful, or infringing content.',
        'Do not attempt to reverse engineer or extract the underlying source code.',
        'Do not use the Service to violate the rights of any third party.',
      ],
    },
    {
      title: '5. Your content',
      body: [
        'You retain ownership of the data, documents, and information you submit to the Service ("Your Content"). You grant us a limited licence to host, store, and process Your Content solely to provide and improve the Service.',
        'You are responsible for ensuring that Your Content is accurate and that you have the right to submit it.',
      ],
    },
    {
      title: '6. Intellectual property',
      body: [
        'The Service, including its software, frameworks, methodology, designs, text, and branding, is owned by GovernWell and protected by intellectual property laws. Except for the rights expressly granted to you in these Terms, no rights are transferred to you.',
      ],
    },
    {
      title: '7. Fees and payment',
      body: [
        'Where the Service is offered on a paid basis, you agree to pay all applicable fees as described at the point of purchase. Unless stated otherwise, fees are non-refundable except as set out in our Refund Policy or as required by law.',
      ],
    },
    {
      title: '8. Availability',
      body: [
        'We aim to keep the Service available and reliable, but we do not guarantee uninterrupted or error-free operation. We may suspend or restrict access for maintenance, updates, or reasons beyond our reasonable control.',
      ],
    },
    {
      title: '9. Disclaimers',
      body: [
        'The Service is provided on an "as is" and "as available" basis. To the fullest extent permitted by law, we make no warranties of any kind, whether express or implied, regarding the Service, including its accuracy, completeness, or fitness for a particular purpose.',
        'Governance outcomes depend on the information you provide and the decisions you make. You remain solely responsible for your governance, compliance, and any decisions taken based on the Service.',
      ],
    },
    {
      title: '10. Limitation of liability',
      body: [
        'To the fullest extent permitted by law, GovernWell shall not be liable for any indirect, incidental, or consequential loss, or for any loss of profits, revenue, data, or goodwill arising from your use of the Service. Nothing in these Terms excludes or limits liability that cannot be excluded or limited under applicable law.',
      ],
    },
    {
      title: '11. Termination',
      body: [
        'You may stop using the Service at any time. We may suspend or terminate your access if you breach these Terms or use the Service in a way that may cause harm to us or others. On termination, your right to use the Service ceases immediately.',
      ],
    },
    {
      title: '12. Changes to these Terms',
      body: [
        'We may update these Terms from time to time. When we do, we will revise the "last updated" date at the top of this page. Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms.',
      ],
    },
    {
      title: '13. Governing law',
      body: [
        'These Terms are governed by the laws of England and Wales, and any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.',
      ],
    },
    {
      title: '14. Contact us',
      body: [
        'If you have any questions about these Terms, please contact us at hello@governwell.co.uk, GovernWell, London, United Kingdom.',
      ],
    },
  ];

  return (
    <div className="brand-theme min-h-screen overflow-x-hidden selection:bg-ink selection:text-lime">
      <Header />

      <section className="dot-pattern px-6 pt-20 pb-16 border-b border-ink/10">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex w-fit items-center gap-2 font-bold text-lg mb-8 hover:underline decoration-4 underline-offset-4 decoration-[#bcd445]"
          >
            <ArrowLeft className="w-5 h-5" /> Back to home
          </button>
          <div className="inline-block bg-white border border-ink/10 rounded-lg shadow-saas-sm px-4 py-2 mb-8 transform -rotate-2">
            <span className="font-bold text-lg uppercase tracking-wide">Legal</span>
          </div>
          <h1 className="font-bold tracking-tight text-5xl md:text-7xl mb-6 leading-[0.9]">
            TERMS &amp;<br />
            <span className="bg-lime px-4 ml-[-16px]">CONDITIONS.</span>
          </h1>
          <p className="font-bold text-sm uppercase tracking-wide">Last updated: {lastUpdated}</p>
        </div>
      </section>

      <section className="px-6 py-20 max-w-4xl mx-auto">
        <div className="bg-cream border border-ink/10 rounded-lg shadow-saas p-6 md:p-10 mb-12">
          <p className="text-lg font-medium">
            Please read these Terms carefully before using GovernWell. By using our website and
            platform, you agree to be bound by the terms set out below.
          </p>
        </div>

        <div className="flex flex-col gap-12">
          {sections.map((section, i) => (
            <div key={i}>
              <h2 className="font-bold tracking-tight text-2xl md:text-3xl mb-4">{section.title}</h2>
              {section.body &&
                section.body.map((p, j) => (
                  <p key={j} className="text-lg font-medium mb-4 leading-relaxed">
                    {p}
                  </p>
                ))}
              {section.list && (
                <ul className="flex flex-col gap-3 mt-2">
                  {section.list.map((item, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-3 bg-white border border-ink/10 rounded-lg p-4 text-lg font-medium"
                    >
                      <span className="w-3 h-3 bg-lime border border-ink/10 rounded-lg shrink-0 mt-2" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsConditions;
